// A5 Portrait — PAWN TICKET with tear-off (orange boxed layout).
import React from 'react';
import { resolvePledgePhotoUrl } from '../../../utils/pledgePhotoUrl';
import TicketCompanyTitle from './ticketCompanyTitle';
import PAWN_TICKET_TYPE from './pawnTicketType';

export const PLEDGE_A5_TEMPLATE_WITH_TEAR = 'A5_PLEDGE_TICKET_WITH_TEAR';

const ORANGE = '#e67e22';
const ORANGE_DARK = '#d35400';
const ORANGE_SOFT = '#fdebd0';
const BORDER = '#333';
const LINE = '#bfbfbf';
const INK = '#111';

const Icon = ({ children, size = 13, color = ORANGE }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth="2.1"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: 'block' }}
  >
    {children}
  </svg>
);

const IdIcon = () => (
  <Icon size={12}>
    <rect x="3" y="5" width="18" height="14" rx="2" />
    <circle cx="9" cy="11" r="2" />
    <path d="M15 10h4M15 14h4" />
  </Icon>
);
const DocIcon = () => (
  <Icon size={12}>
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <path d="M14 2v6h6M8 13h8M8 17h5" />
  </Icon>
);
const UserIcon = ({ size = 12 }) => (
  <Icon size={size}>
    <circle cx="12" cy="8" r="3.5" />
    <path d="M5 19c1.2-3.2 3.8-5 7-5s5.8 1.8 7 5" />
  </Icon>
);
const RupeeIcon = () => (
  <Icon size={12}>
    <path d="M7 5h10M7 9h10M7 5c4 0 7 2 7 6s-3 6-7 6" />
    <path d="M7 21l6-8" />
  </Icon>
);
const GemIcon = () => (
  <Icon size={12}>
    <path d="M6 9l6-5 6 5-6 11L6 9z" />
    <path d="M6 9h12" />
  </Icon>
);
const CamIcon = () => (
  <Icon size={18}>
    <path d="M4 8h3l2-2h6l2 2h3v11H4z" />
    <circle cx="12" cy="13" r="3.5" />
  </Icon>
);
const ShieldIcon = () => (
  <Icon size={11}>
    <path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6l8-3z" />
    <path d="M9 12l2 2 4-4" />
  </Icon>
);
const ScissorsIcon = () => (
  <Icon size={13} color="#666">
    <circle cx="6" cy="6" r="2.5" />
    <circle cx="6" cy="18" r="2.5" />
    <path d="M8 7.5 L20 20 M8 16.5 L20 4" />
  </Icon>
);

const box = {
  border: `1px solid ${LINE}`,
  borderRadius: 6,
  padding: '6px 8px',
  background: '#fff',
  minWidth: 0,
  maxWidth: '100%',
  overflow: 'hidden',
  boxSizing: 'border-box',
};

const Field = ({ label, children, last }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'flex-start',
      gap: 4,
      marginBottom: last ? 0 : 3,
      fontSize: PAWN_TICKET_TYPE.detail.fontSize,
      lineHeight: 1.35,
    }}
  >
    <div style={{ width: 96, flexShrink: 0, color: '#444', fontSize: PAWN_TICKET_TYPE.fieldLabel.fontSize, fontWeight: PAWN_TICKET_TYPE.fieldLabel.fontWeight }}>{label}</div>
    <div style={{ flexShrink: 0, color: '#444' }}>:</div>
    <div style={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere', wordBreak: 'break-word' }}>{children}</div>
  </div>
);

const SectionHead = ({ icon, children }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6 }}>
    <span
      style={{
        width: 18,
        height: 18,
        borderRadius: '50%',
        background: ORANGE_SOFT,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      {icon}
    </span>
    <span style={{ color: ORANGE_DARK, fontSize: PAWN_TICKET_TYPE.sectionHeading.fontSize, fontWeight: PAWN_TICKET_TYPE.sectionHeading.fontWeight, letterSpacing: 0.3 }}>{children}</span>
  </div>
);

const PledgePrintA5WithTear = ({ pledgeData, companyData }) => {
  if (!pledgeData) return null;

  const formatDate = (date) => {
    if (!date) return '—';
    return new Date(date).toLocaleDateString('en-GB');
  };
  const formatAmount = (amount) => {
    const n = parseFloat(amount);
    if (!Number.isFinite(n)) return '0';
    return n.toLocaleString('en-IN');
  };
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero';
    const ones = [
      '', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine',
      'Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen',
      'Seventeen', 'Eighteen', 'Nineteen',
    ];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const w = (n) => {
      if (n < 20) return ones[n];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 ? ' ' + ones[n % 10] : '');
      if (n < 1000) return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 ? ' ' + w(n % 100) : '');
      if (n < 100000) return w(Math.floor(n / 1000)) + ' Thousand' + (n % 1000 ? ' ' + w(n % 1000) : '');
      if (n < 10000000) return w(Math.floor(n / 100000)) + ' Lakh' + (n % 100000 ? ' ' + w(n % 100000) : '');
      return w(Math.floor(n / 10000000)) + ' Crore' + (n % 10000000 ? ' ' + w(n % 10000000) : '');
    };
    return 'Rupees ' + w(Math.floor(num)) + ' Only';
  };
  const resolvePhotoSrc = (stored) => {
    if (stored == null || stored === '') return null;
    if (typeof stored === 'string' && stored.startsWith('blob:')) return stored;
    if (stored instanceof File || stored instanceof Blob) {
      try {
        return URL.createObjectURL(stored);
      } catch {
        return null;
      }
    }
    return resolvePledgePhotoUrl(stored);
  };

  const c = pledgeData.customer || {};
  const up = (v) => String(v || '').trim().toUpperCase();
  const rel = up(c.relation);
  const showRel = rel && rel !== 'F/O';
  const pawnerName = [up(c.firstName), showRel ? rel : '', showRel ? up(c.relationName) : '', up(c.lastName)].filter(Boolean).join(' ') || '—';
  const address = [c.address, c.village, c.atPost].map((x) => up(x)).filter(Boolean).join(', ') || '—';
  const ticketNo = pledgeData.loanDetails?.ticketNo || '—';
  const principal = parseFloat(pledgeData.loanDetails?.principalAmount) || 0;
  const duration = Number(pledgeData.loanDetails?.duration) || 12;
  const roi = pledgeData.loanDetails?.rateOfInterest || '0';
  const loanDate = formatDate(pledgeData.date);

  const wt = (a, keys) => {
    for (const k of keys) {
      const n = parseFloat(a?.[k]);
      if (Number.isFinite(n)) return n;
    }
    return 0;
  };
  const articleRows = (pledgeData.articles || []).map((a, i) => {
    const gross = wt(a, ['grossWt', 'grossWeight']);
    const net = wt(a, ['netWt', 'netWeight']);
    return {
      no: i + 1,
      desc: String(a.description || a.articleTitle || a.title || a.name || 'Article').toUpperCase(),
      qty: parseFloat(a.qty ?? a.quantity) || 1,
      gross,
      deduction: Math.max(0, gross - net),
      net,
      purity: a.purity || '—',
      value: parseFloat(a.valueTotal) || 0,
    };
  });
  const displayRows = articleRows;
  const totals = articleRows.reduce(
    (s, r) => ({
      qty: s.qty + r.qty,
      gross: s.gross + r.gross,
      deduction: s.deduction + r.deduction,
      net: s.net + r.net,
      value: s.value + r.value,
    }),
    { qty: 0, gross: 0, deduction: 0, net: 0, value: 0 }
  );

  const clientPhotoSrc = resolvePhotoSrc(pledgeData.clientPhoto);
  const articlePhotoSrc = resolvePhotoSrc(pledgeData.articlePhoto);
  const thumbImageSrc = pledgeData.fingerprint?.image
    ? pledgeData.fingerprint.image.startsWith('data:')
      ? pledgeData.fingerprint.image
      : `data:image/png;base64,${pledgeData.fingerprint.image}`
    : null;

  const th = {
    fontSize: PAWN_TICKET_TYPE.articleHeading.fontSize,
    fontWeight: PAWN_TICKET_TYPE.articleHeading.fontWeight,
    color: ORANGE_DARK,
    textTransform: 'uppercase',
    padding: '4px 2px',
    borderBottom: `2px solid ${ORANGE}`,
    textAlign: 'right',
    whiteSpace: 'normal',
    overflowWrap: 'anywhere',
    lineHeight: 1.2,
  };
  const td = {
    fontSize: PAWN_TICKET_TYPE.articleValue.fontSize,
    padding: '4px 2px',
    borderBottom: '1px solid #e8e8e8',
    textAlign: 'right',
    verticalAlign: 'middle',
    height: 18,
    overflowWrap: 'anywhere',
    wordBreak: 'break-word',
  };

  const hl = {
    fontWeight: 800,
  };

  const MetaCard = ({ icon, children }) => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        border: `1px solid ${BORDER}`,
        borderRadius: 5,
        padding: '2px 6px',
        fontSize: PAWN_TICKET_TYPE.licenceForm.fontSize,
        color: '#333',
        background: '#fff',
        whiteSpace: 'nowrap',
        width: '100%',
        boxSizing: 'border-box',
        flexShrink: 0,
      }}
    >
      {icon}
      {children}
    </div>
  );

  const Slip = ({ extra }) => (
    <div style={{ ...box, flex: 1, padding: '4px 8px' }}>
      <div style={{ fontSize: PAWN_TICKET_TYPE.tearOff.fontSize, lineHeight: 1.3 }}>
        <div>
          <span style={{ color: '#555' }}>Ticket No.</span>
          <span style={{ ...hl, marginLeft: 8 }}>{ticketNo}</span>
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{ color: '#555' }}>Name</span>
          <span style={{ ...hl, marginLeft: 8 }}>{pawnerName}</span>
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{ color: '#555' }}>Amount</span>
          <span style={{ ...hl, marginLeft: 8 }}>₹ {formatAmount(principal)}</span>
        </div>
        <div style={{ marginTop: 2 }}>
          <span style={{ color: '#555' }}>Date</span>
          <span style={{ ...hl, marginLeft: 8 }}>{loanDate}</span>
        </div>
        {extra}
      </div>
    </div>
  );

  return (
    <div
      className="pledge-print-a5-with-tear"
      style={{
        width: '100%',
        maxWidth: '100%',
        height: '100%',
        minHeight: '100%',
        overflow: 'visible',
        boxSizing: 'border-box',
        padding: '0 3mm 3mm',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: INK,
        background: '#fff',
        lineHeight: 1.3,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div
        style={{
          flex: '1 1 auto',
          minHeight: 0,
          minWidth: 0,
          overflow: 'visible',
          border: `1.2px solid ${BORDER}`,
          borderRadius: 2,
          padding: '3mm 4mm 2mm',
          display: 'flex',
          flexDirection: 'column',
          boxSizing: 'border-box',
        }}
      >
        <div style={{ marginBottom: 4, paddingTop: 1, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 18 }}>
            <div style={{ flex: '1 1 auto', minWidth: 0 }}>
              <TicketCompanyTitle name={companyData?.companyName} />
            </div>
            <div
              style={{
                flex: '0 0 auto',
                fontFamily: '"Times New Roman", Times, serif',
                fontWeight: PAWN_TICKET_TYPE.pawnTicket.fontWeight,
                letterSpacing: 0.8,
                fontSize: PAWN_TICKET_TYPE.pawnTicket.fontSize,
                lineHeight: 1,
                whiteSpace: 'nowrap',
              }}
            >
              PAWN TICKET
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginTop: 2 }}>
          <div style={{ flex: 1, textAlign: 'center', minWidth: 0 }}>
            <div style={{ fontSize: PAWN_TICKET_TYPE.address.fontSize, fontWeight: PAWN_TICKET_TYPE.address.fontWeight, color: '#333', lineHeight: 1.25 }}>
              {companyData?.address || 'Company Address'}
            </div>
          </div>
          <div style={{ flex: '0 0 auto', width: 'max-content', textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'stretch' }}>
            <MetaCard icon={<IdIcon />}>Licence No.: {companyData?.licenseNumber || '—'}</MetaCard>
            <div style={{ height: 2 }} />
            <MetaCard icon={<DocIcon />}>FORM &apos;F&apos; — SEE RULE 12</MetaCard>
          </div>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 6, minWidth: 0, flexShrink: 0 }}>
          <div style={{ ...box, flex: 1, padding: '8px 10px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: PAWN_TICKET_TYPE.ticketDateLabel.fontSize, color: '#333', fontWeight: PAWN_TICKET_TYPE.ticketDateLabel.fontWeight, letterSpacing: 0.3, whiteSpace: 'nowrap' }}>
              PAWN TICKET NO.:-
            </span>
            <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: PAWN_TICKET_TYPE.ticketDateValue.fontSize, fontWeight: PAWN_TICKET_TYPE.ticketDateValue.fontWeight, lineHeight: 1 }}>
              {ticketNo}
            </span>
          </div>
          <div style={{ ...box, flex: 1, padding: '8px 10px', display: 'flex', alignItems: 'baseline', gap: 8 }}>
            <span style={{ fontSize: PAWN_TICKET_TYPE.ticketDateLabel.fontSize, color: '#333', fontWeight: PAWN_TICKET_TYPE.ticketDateLabel.fontWeight, letterSpacing: 0.3 }}>DATE:-</span>
            <span style={{ fontFamily: '"Times New Roman", Times, serif', fontSize: PAWN_TICKET_TYPE.ticketDateValue.fontSize, fontWeight: PAWN_TICKET_TYPE.ticketDateValue.fontWeight, lineHeight: 1 }}>
              {loanDate}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, marginBottom: 6, minWidth: 0, flexShrink: 0 }}>
          <div style={{ ...box, flex: 1 }}>
            <SectionHead icon={<UserIcon />}>1. PAWNER DETAILS</SectionHead>
            <Field label="Name of Pawner"><span style={{ fontWeight: PAWN_TICKET_TYPE.customerName.fontWeight, fontSize: PAWN_TICKET_TYPE.customerName.fontSize }}>{pawnerName}</span></Field>
            <Field label="Address">{address}</Field>
            <Field label="Mobile No." last>{c.mobileNo || '—'}</Field>
          </div>
          <div style={{ ...box, flex: 1 }}>
            <SectionHead icon={<RupeeIcon />}>2. LOAN DETAILS</SectionHead>
            <Field label="Principal Amount">
              <span style={{ ...hl, fontSize: PAWN_TICKET_TYPE.detail.fontSize }}>{formatAmount(principal)}</span>
            </Field>
            <Field label="Amount in Words">{numberToWords(principal)}</Field>
            <Field label="Redemption Period">{`${duration} Month${Number(duration) === 1 ? '' : 's'}`}</Field>
            <Field label="Rate of Interest" last>{`${roi}% per annum`}</Field>
          </div>
        </div>

        <div style={{ ...box, marginBottom: 6, flexShrink: 0 }}>
          <SectionHead icon={<GemIcon />}>3. ARTICLES PLEDGED</SectionHead>
          <table style={{ width: '100%', maxWidth: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <thead>
              <tr>
                <th style={{ ...th, textAlign: 'left', width: '6%' }}>No.</th>
                <th style={{ ...th, textAlign: 'left', width: '24%' }}>Description</th>
                <th style={{ ...th, width: '8%' }}>Qty</th>
                <th style={{ ...th, width: '14%' }}>Gross Wt. (g)</th>
                <th style={{ ...th, width: '14%' }}>Net Wt. (g)</th>
                <th style={{ ...th, width: '16%' }}>Approx Purity</th>
                <th style={{ ...th, width: '18%' }}>Value</th>
              </tr>
            </thead>
            <tbody>
              {displayRows.map((r) =>
                r.empty ? (
                  <tr key={`e-${r.no}`}>
                    <td style={{ ...td, textAlign: 'left', color: '#bbb' }}>{r.no}</td>
                    <td colSpan={6} style={td} />
                  </tr>
                ) : (
                  <tr key={r.no}>
                    <td style={{ ...td, textAlign: 'left' }}>{r.no}</td>
                    <td style={{ ...td, textAlign: 'left', fontWeight: 700 }}>{r.desc}</td>
                    <td style={td}>{r.qty}</td>
                    <td style={td}>{r.gross.toFixed(3)}</td>
                    <td style={td}>{r.net.toFixed(3)}</td>
                    <td style={td}>{r.purity === '—' || String(r.purity).includes('%') ? r.purity : `${r.purity}%`}</td>
                    <td style={td}>{formatAmount(r.value)}</td>
                  </tr>
                )
              )}
              <tr>
                <td colSpan={2} style={{ ...td, textAlign: 'left', fontWeight: 800, background: ORANGE_SOFT, borderBottom: 'none' }}>
                  TOTAL
                </td>
                <td style={{ ...td, fontWeight: 800, background: ORANGE_SOFT, borderBottom: 'none' }}>{totals.qty || ''}</td>
                <td style={{ ...td, fontWeight: 800, background: ORANGE_SOFT, borderBottom: 'none' }}>{totals.gross.toFixed(3)}</td>
                <td style={{ ...td, fontWeight: 800, background: ORANGE_SOFT, borderBottom: 'none' }}>{totals.net.toFixed(3)}</td>
                <td style={{ ...td, background: ORANGE_SOFT, borderBottom: 'none' }} />
                <td style={{ ...td, fontWeight: 800, background: ORANGE_SOFT, borderBottom: 'none' }}>{formatAmount(totals.value)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8, padding: '0 36px', flex: '0 0 auto' }}>
          {[
            { src: clientPhotoSrc, label: 'Pawner Photograph', icon: <UserIcon size={20} /> },
            { src: articlePhotoSrc, label: 'Pledged Article Photograph', icon: <CamIcon /> },
          ].map((p) => (
            <div key={p.label} style={{ width: '32mm', flex: '0 0 auto', textAlign: 'center' }}>
              <div
                style={{
                  width: '32mm',
                  height: '32mm',
                  border: `1.5px dashed ${ORANGE}`,
                  borderRadius: 6,
                  background: '#fff8f0',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  overflow: 'hidden',
                }}
              >
                {p.src ? (
                  <img src={p.src} alt={p.label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                ) : (
                  p.icon
                )}
              </div>
              <div style={{ fontSize: PAWN_TICKET_TYPE.photoCaption.fontSize, fontWeight: PAWN_TICKET_TYPE.photoCaption.fontWeight, color: '#555', marginTop: 2 }}>{p.label}</div>
            </div>
          ))}
        </div>

        <div style={{ marginTop: 'auto', flexShrink: 0 }}>
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 2, flexShrink: 0 }}>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div style={{ borderBottom: `1px solid ${INK}`, height: 32 }} />
            <div style={{ fontSize: PAWN_TICKET_TYPE.signatureCaption.fontSize, fontWeight: PAWN_TICKET_TYPE.signatureCaption.fontWeight, color: '#555', marginTop: 2 }}>
              Signature of Pawnbroker / Authorized Agent
            </div>
          </div>
          <div style={{ flex: 1, textAlign: 'center' }}>
            <div
              style={{
                borderBottom: `1px solid ${INK}`,
                height: 32,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
              }}
            >
              {thumbImageSrc ? (
                <img src={thumbImageSrc} alt="Thumb" style={{ maxHeight: 36, maxWidth: 72, objectFit: 'contain' }} />
              ) : null}
            </div>
            <div style={{ fontSize: PAWN_TICKET_TYPE.signatureCaption.fontSize, fontWeight: PAWN_TICKET_TYPE.signatureCaption.fontWeight, color: '#555', marginTop: 2 }}>
              Signature / Left Thumb Impression of Pawner
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, fontSize: PAWN_TICKET_TYPE.detail.fontSize, color: '#555' }}>
          <ShieldIcon />
          Please retain this pawn ticket and produce it while redeeming the pledged articles.
        </div>
        </div>
      </div>

      <div style={{ flex: '0 0 auto', marginTop: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3 }}>
          <ScissorsIcon />
          <div style={{ flex: 1, borderTop: '1.6px dashed #777' }} />
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Slip />
          <Slip
            extra={
              <div style={{ marginTop: 2 }}>
                <span style={{ color: '#555' }}>Time</span>
                <strong style={{ marginLeft: 8 }}>1 Year</strong>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
};

export default PledgePrintA5WithTear;
